import React, { useEffect, useRef } from 'react';
import '../App.css';
import '../components/Hero.css';

const DOCUMENTS = [
  {
    title: 'Scyla Domain Partner Agreement',
    subtitle: 'Template v1.0 -- April 2026',
    description: 'The founding document for domain partnerships. Defines ownership of the Cascade, Domain Heads, Genesis Cells, and the Strait Doctrine. Covers architecture access, disconnection rights, change of control, and revenue structure.',
    file: '/assets/SCYLA_PARTNER_AGREEMENT.pdf',
    pages: 8,
    articles: [
      'Article I -- Definitions (Cascade, Domain Heads, Genesis Cells)',
      'Article II -- Ownership (the body is ours, the heads are yours)',
      'Article III -- Architecture and Access (API only, your calls, your heads)',
      'Article IV -- Licensing and Revenue (open source base, licensed verticals)',
      'Article V -- Disconnection and Exit (heads delivered, cascade continues)',
      'Article VI -- Change of Control (acquirers don\'t inherit access)',
      'Article VII -- Protection of the Architecture (patents acknowledged)',
      'Article VIII -- The Strait Doctrine (the founding principle)',
    ],
    patents: ['63/939,190', '63/962,385', '63/988,485', '64/034,536'],
    status: 'active',
  },
];

export default function DocumentsPage() {
  const bgRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (bgRef.current) {
        const scrolled = window.scrollY;
        bgRef.current.style.transform = `scale(1.05) translateY(${scrolled * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div>
      <section className="hero" id="top" style={{ minHeight: '50vh' }}>
        <div className="hero-bg" ref={bgRef} style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/hero-bg.png)` }} />
        <div className="hero-overlay" />
        <div className="hero-content">
          <img src="/assets/NexusLogo.png" alt="Nexus Concordat" className="hero-logo" />
          <span className="hero-label">Nexus Concordat &middot; Legal</span>
          <h1 className="hero-title">
            Documents
          </h1>
          <p className="hero-sub" style={{ maxWidth: 550 }}>
            Partnership agreements, templates, and founding documents for Scyla domain integration.
          </p>
        </div>
      </section>

      <section style={{ padding: '3rem 2rem', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {DOCUMENTS.map((doc, i) => (
            <div key={i} style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: '2rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{
                    fontFamily: "'Comfortaa', cursive",
                    fontSize: '1.3rem',
                    fontWeight: 500,
                    color: 'var(--text)',
                    marginBottom: '0.3rem',
                  }}>{doc.title}</h2>
                  <p style={{
                    fontSize: '0.75rem',
                    color: 'var(--gold)',
                    letterSpacing: '0.05em',
                    marginBottom: '1rem',
                  }}>{doc.subtitle} &middot; {doc.pages} pages</p>
                  <p style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-sub)',
                    lineHeight: 1.7,
                    marginBottom: '1.5rem',
                  }}>{doc.description}</p>
                </div>
                <a href={doc.file} target="_blank" rel="noopener noreferrer" style={{
                  display: 'inline-block',
                  padding: '0.7rem 1.8rem',
                  fontFamily: "'Comfortaa', cursive",
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: 'var(--bg)',
                  background: 'var(--gold)',
                  borderRadius: 8,
                  textDecoration: 'none',
                  letterSpacing: '0.05em',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>Download PDF</a>
              </div>

              <div style={{
                borderTop: '1px solid var(--border)',
                paddingTop: '1.2rem',
                marginTop: '0.5rem',
              }}>
                <h3 style={{
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: 'var(--gold)',
                  marginBottom: '0.8rem',
                }}>Contents</h3>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '0.4rem',
                }}>
                  {doc.articles.map((article, j) => (
                    <li key={j} style={{
                      fontSize: '0.78rem',
                      color: 'var(--text-sub)',
                      lineHeight: 1.6,
                      paddingLeft: '1rem',
                      position: 'relative',
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        color: 'var(--gold-dim)',
                      }}>&bull;</span>
                      {article}
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{
                borderTop: '1px solid var(--border)',
                paddingTop: '1rem',
                marginTop: '1rem',
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}>
                <span style={{
                  fontSize: '0.6rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: 'var(--text-dim)',
                  marginRight: '0.5rem',
                }}>Patents Referenced:</span>
                {doc.patents.map((p, k) => (
                  <span key={k} style={{
                    fontSize: '0.65rem',
                    padding: '0.2rem 0.5rem',
                    background: 'rgba(201,168,76,0.08)',
                    border: '1px solid rgba(201,168,76,0.15)',
                    borderRadius: 4,
                    color: 'var(--gold)',
                  }}>{p}</span>
                ))}
              </div>
            </div>
          ))}

          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: 'var(--text-dim)',
            fontSize: '0.75rem',
            lineHeight: 1.8,
          }}>
            <p style={{ fontStyle: 'italic', color: 'var(--gold-dim)', marginBottom: '0.5rem' }}>
              "You don't optimize around it. You submit to the passage or you don't pass at all."
            </p>
            <p>All documents are templates. Final terms are negotiated per partnership.</p>
            <p>Contact: caelsereith@aetherprotocols.com</p>
          </div>
        </div>
      </section>
    </div>
  );
}
